import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	BulkFlightActionDocument,
	BulkFlightActionFieldsFragment,
	BulkFlightActionInput,
	BulkFlightActionPreviewDocument,
	BulkFlightSelectionDocument,
	FlightCountersDocument,
	RecentBulkFlightActionsDocument,
	UndoBulkFlightActionDocument,
	UpcomingFlightScope,
	UserRole,
} from '../../graphql/generated/gen-types';

// 221 bulk flight actions: the selection on the upcoming flight list, shared between the flight
// table (checkbox column) and the bulk bar above it, plus the GraphQL operations.

export const BULK_LIMIT = 100;

export interface FlightListArgs {
	filter: string[];
	filterRange: { startDate: any; endDate: any } | null;
	freeSpots: number;
	// 235: the flight list's status — Upcoming (CURRENT) or Not closed (older) (OLDER)
	scope: UpcomingFlightScope | null;
}

@Injectable({
	providedIn: 'root',
})
export class FlightBulkService {
	private selection = new Map<string, any>(); // flightId -> flight date
	readonly selected$ = new BehaviorSubject<number>(0);
	// bulk mode: the checkbox column and the bulk bar only show after the operator opens it
	readonly mode$ = new BehaviorSubject<boolean>(false);
	// "select all matching" found more than BULK_LIMIT flights (the count), or null
	readonly tooManyMatches$ = new BehaviorSubject<number>(null);
	readonly loadingMatches$ = new BehaviorSubject<boolean>(false);
	// "select all matching" found nothing
	readonly noMatches$ = new Subject<void>();
	readonly error$ = new Subject<string>();
	// a tick beyond BULK_LIMIT was refused
	readonly limitReached$ = new Subject<void>();
	// the upcoming flight table reloads when this fires (after an action or undo)
	readonly reload$ = new Subject<void>();
	// registered by the upcoming flight table: the exact arguments of its first page
	listArgsProvider: () => FlightListArgs = null;

	constructor(private apollo: Apollo) {}

	canBulk(userRole: UserRole): boolean {
		return userRole === UserRole.Own || userRole === UserRole.Opr;
	}

	// ------------------------------------------------------------ mode

	get active(): boolean {
		return this.mode$.value;
	}

	setMode(active: boolean): void {
		if (!active) this.clear();
		if (this.mode$.value !== active) this.mode$.next(active);
	}

	// ------------------------------------------------------------ selection

	isSelected(flightId: string): boolean {
		return this.selection.has(flightId);
	}

	get count(): number {
		return this.selection.size;
	}

	get ids(): string[] {
		return [...this.selection.keys()];
	}

	toggle(flight: { id: string; date?: any }): boolean {
		if (this.selection.has(flight.id)) {
			this.selection.delete(flight.id);
		} else if (this.selection.size >= BULK_LIMIT) {
			this.limitReached$.next();
			return false;
		} else {
			this.selection.set(flight.id, flight.date ?? null);
		}
		if (this.tooManyMatches$.value !== null) this.tooManyMatches$.next(null);
		this.selected$.next(this.selection.size);
		return true;
	}

	setAll(flightIds: string[]): void {
		this.selection = new Map(flightIds.slice(0, BULK_LIMIT).map((id) => [id, null]));
		if (this.tooManyMatches$.value !== null) this.tooManyMatches$.next(null);
		this.selected$.next(this.selection.size);
	}

	clear(): void {
		if (this.tooManyMatches$.value !== null) this.tooManyMatches$.next(null);
		if (this.selection.size === 0) return;
		this.selection.clear();
		this.selected$.next(0);
	}

	// select every flight matching the list's current filter (≤ BULK_LIMIT), else report the count
	selectAllMatching(): void {
		this.loadingMatches$.next(true);
		this.tooManyMatches$.next(null);
		this.fetchMatching().subscribe({
			next: (result) => {
				this.loadingMatches$.next(false);
				if (result.total > result.limit) {
					this.clear();
					this.tooManyMatches$.next(result.total);
				} else if (result.flightIds.length === 0) {
					this.noMatches$.next();
				} else {
					this.setAll(result.flightIds);
				}
			},
			error: (err) => {
				this.loadingMatches$.next(false);
				this.error$.next(err.message);
			},
		});
	}

	// header checkbox / bar toggle: deselect when something is selected, else select all matching
	toggleAll(): void {
		if (this.selection.size > 0) this.clear();
		else this.selectAllMatching();
	}

	// ------------------------------------------------------------ operations

	private fetchMatching(): Observable<{ total: number; flightIds: string[]; limit: number }> {
		const args = this.listArgsProvider?.();
		return this.apollo
			.query<any>({
				query: BulkFlightSelectionDocument,
				variables: {
					filter: args?.filter ?? [],
					filterRange: args?.filterRange ?? null,
					freeSpots: args?.freeSpots ?? 0,
					scope: args?.scope ?? null,
				},
				fetchPolicy: 'network-only',
			})
			.pipe(map((result) => result.data.bulkFlightSelection));
	}

	preview(flightIds: string[], input: BulkFlightActionInput): Observable<any> {
		return this.apollo
			.query<any>({
				query: BulkFlightActionPreviewDocument,
				variables: { flightIds, input },
				fetchPolicy: 'network-only',
			})
			.pipe(map((result) => result.data.bulkFlightActionPreview));
	}

	execute(flightIds: string[], input: BulkFlightActionInput): Observable<BulkFlightActionFieldsFragment> {
		return this.apollo
			.mutate<any>({
				mutation: BulkFlightActionDocument,
				variables: { flightIds, input },
				refetchQueries: [FlightCountersDocument],
			})
			.pipe(map((result) => result.data.bulkFlightAction));
	}

	undo(actionId: string): Observable<BulkFlightActionFieldsFragment> {
		return this.apollo
			.mutate<any>({
				mutation: UndoBulkFlightActionDocument,
				variables: { actionId },
				refetchQueries: [FlightCountersDocument],
			})
			.pipe(map((result) => result.data.undoBulkFlightAction));
	}

	recent(): Observable<BulkFlightActionFieldsFragment[]> {
		return this.apollo
			.query<any>({ query: RecentBulkFlightActionsDocument, fetchPolicy: 'network-only' })
			.pipe(map((result) => result.data.recentBulkFlightActions));
	}
}
